// src/pages/SchedulesListPage.jsx
import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  ButtonGroup,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { getBackendBaseUrl } from "../../utils/envUtils";
import ScheduleControlButton from "../../components/scheduler/ScheduleControlButton"; // 👈 import the new button

export default function SchedulesListPage() {
  const history = useHistory();
  const location = useLocation();
  const HOST = getBackendBaseUrl();
  const SCHEDULE_ENDPOINT = `${HOST}/api/v1/upsell-schedule`;
  const token = Cookies.get("jwtToken");

  const [schedules, setSchedules] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const fetchSchedules = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(SCHEDULE_ENDPOINT, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error("Failed to fetch schedules");

      const data = await res.json();
      const normalize = (s) => ({
        id: s.id ?? s._id ?? s.scheduleId,
        title: s.title ?? "",
        isActive: !!s.isActive,
        ...s,
      });
      setSchedules(Array.isArray(data) ? data.map(normalize) : []);
    } catch (err) {
      setError(err.message || "Failed to fetch schedules");
    } finally {
      setLoading(false);
    }
  }, [SCHEDULE_ENDPOINT, token]);

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${SCHEDULE_ENDPOINT}/${id}`, {
        method: "DELETE",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error("Failed to delete schedule");
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  React.useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Typography variant="h4">Upsell Schedules</Typography>

      {/* 👇 Add the control button here */}
      <ScheduleControlButton />

      <Card>
        <CardHeader title="All Schedules" />
        <CardContent sx={{ p: 0 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push("/upsell/schedules/new")}
            sx={{ mb: 2 }}
          >
            Create New Schedule
          </Button>
          {loading && <Typography sx={{ p: 2 }}>Loading…</Typography>}
          {error && <Typography color="error" sx={{ p: 2 }}>{error}</Typography>}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.title || `Schedule #${s.id}`}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.isActive ? "Active" : "Inactive"}
                      color={s.isActive ? "success" : "default"}
                      size="small"
                      variant={s.isActive ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <ButtonGroup variant="outlined" size="small">
                      <Button
                        onClick={() =>
                          history.push({
                            pathname: `/upsell/schedules/${s.id}/edit`,
                            state: { schedule: s },
                          })
                        }
                      >
                        Update
                      </Button>
                      <Button
                        color="error"
                        onClick={() => handleDelete(s.id)}
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Stack>
  );
}
